import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, MapPin, Clock } from 'lucide-react';
import BlogEmailCapture from '../components/BlogEmailCapture';
import MidtownStudioCallout from '../components/MidtownStudioCallout';

const BlogPostHowDoIKnow = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'How Do I Know If I Need a Massage? | Ayumi Suzuki';
    return () => { document.title = prev; };
  }, []);

  const isoDate = '2026-02-13';
  const canonical = 'https://ayumisuzuki.com/blog/how-do-i-know-if-i-need-a-massage';

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50" data-testid="how-do-i-know-blog-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: 'How Do I Know If I Need a Massage? A Sacramento Therapist\'s Honest Take',
            description:
              'How do you actually know if you need a massage? A personal, honest answer from certified midtown Sacramento therapist Ayumi Suzuki — the signals your body is sending, and what to do about them.',
            image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1600&auto=format&fit=crop&q=80',
            author: { '@type': 'Person', name: 'Ayumi Suzuki', url: 'https://ayumisuzuki.com/about', jobTitle: 'Certified Massage Therapist' },
            publisher: {
              '@type': 'Organization',
              name: 'Ayumi Suzuki Massage Therapy',
              logo: { '@type': 'ImageObject', url: 'https://customer-assets.emergentagent.com/job_suzuki-2026-refresh/artifacts/hdysr3f5_ayumi1%20logo.jpg' },
            },
            datePublished: isoDate,
            dateModified: isoDate,
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
            articleSection: 'Personal Notes',
            keywords: 'how do I know if I need a massage, signs I need a massage, when to book a massage, Ayumi Suzuki',
            about: [{ '@type': 'Thing', name: 'Massage Therapy' }, { '@type': 'Place', name: 'Sacramento, California' }],
            inLanguage: 'en-US',
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'QAPage',
            mainEntity: {
              '@type': 'Question',
              name: 'How do I know if I need a massage?',
              text: 'What are the signs that my body actually needs a massage right now?',
              answerCount: 1,
              author: { '@type': 'Person', name: 'Ayumi Suzuki' },
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'You probably need a massage if you have persistent neck, shoulder, or back tightness that isn\'t going away, if your sleep quality has dropped, if you\'re feeling wound-up even on rest days, if your range of motion has quietly narrowed, if you\'re getting more tension headaches, or if you find yourself Googling "do I need a massage" — that last one is usually the strongest signal. Book at Ayumi Suzuki\'s midtown Sacramento studio (2528 I Street, Sacramento, CA 95816). Sessions run 30–120 min ($55–$200).',
                author: { '@type': 'Person', name: 'Ayumi Suzuki', jobTitle: 'Certified Massage Therapist' },
                dateCreated: isoDate,
                upvoteCount: 0,
                url: canonical,
              },
            },
          }),
        }}
      />

      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/blog" className="inline-flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors duration-300 group mb-4" data-testid="back-to-blog-link">
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
            <span>Back to Blog</span>
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
            <span className="flex items-center" data-testid="post-date"><Calendar className="w-4 h-4 mr-2" /><time dateTime={isoDate}>February 13, 2026</time></span>
            <span className="flex items-center"><User className="w-4 h-4 mr-2" />Ayumi Suzuki</span>
            <span className="flex items-center"><MapPin className="w-4 h-4 mr-2" />Midtown Sacramento</span>
            <span className="flex items-center"><Clock className="w-4 h-4 mr-2" />6 min read</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-gray-800 mb-6 leading-tight" data-testid="post-title">
            How Do I Know If I Need a Massage?
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            If you're asking, the answer is almost always yes. But since I want to be more useful than that, here's the longer version.
          </p>
        </header>

        <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
          <img src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1600&auto=format&fit=crop&q=80" alt="Silhouette of a person in a mindful pose at sunset — signs you need a massage" className="w-full h-96 object-cover" />
        </div>

        <div className="bg-white border border-teal-100 rounded-2xl p-6 mb-12 shadow-sm">
          <h2 className="text-xl font-serif text-teal-700 mb-2">The Short Answer</h2>
          <p className="text-gray-700 leading-relaxed">
            You probably need one if any of these are true: persistent neck or upper-back tightness, sleep that isn't restorative, feeling wound-up on rest days, quiet loss of range of motion, more headaches, or the fact that you're on this page reading this. Book with me at <strong>2528 I Street, midtown Sacramento</strong> — or text <a href="sms:+14156789133" className="text-teal-600 hover:underline">415-678-9133</a>.
          </p>
        </div>

        <div className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-a:text-teal-700">
          <h2>The Real Answer: Your Body Has Been Trying to Tell You</h2>
          <p>
            Most of my clients don't book because someone told them they needed a massage. They book because their body has been sending signals for weeks — sometimes months — and they finally paid attention. Massage therapy isn't a luxury response to a bad week. It's a maintenance tool for a body that never actually stops working.
          </p>

          <h2>The 10 Signals I Hear Most Often</h2>
          <ol>
            <li><strong>"My shoulders live up by my ears now."</strong> Chronic upper-trap tension.</li>
            <li><strong>"I can't turn my head as far as I used to."</strong> Quiet loss of cervical range of motion.</li>
            <li><strong>"My low back hurts just from sitting."</strong> Postural strain that's crossed into pain.</li>
            <li><strong>"I wake up already tired."</strong> Sleep isn't repairing because the nervous system stays activated.</li>
            <li><strong>"I clench my jaw at night."</strong> Stress is being stored in the face and neck.</li>
            <li><strong>"I keep getting tension headaches."</strong> Neck and traps aren't releasing between stressors.</li>
            <li><strong>"I feel wound-up even on my days off."</strong> Baseline stress is elevated even without a trigger.</li>
            <li><strong>"My workouts hurt more than they should."</strong> Recovery isn't keeping up with your training.</li>
            <li><strong>"I feel disconnected from my body."</strong> A big one. Body awareness has quietly gone offline.</li>
            <li><strong>"I've been Googling if I need a massage."</strong> This is the most reliable signal there is.</li>
          </ol>

          <h2>Signs You Don't <em>Need</em> One But Would Still Benefit</h2>
          <ul>
            <li>You've been consistent with self-care but want a deeper reset</li>
            <li>You're heading into a stressful season (travel, holidays, deadline)</li>
            <li>You want to try it because you've never had one</li>
            <li>Someone gave you a gift certificate (do not let it expire)</li>
          </ul>

          <h2>Signs a Doctor First, Then Massage</h2>
          <p>
            Massage is powerful but not a substitute for medical evaluation. See a doctor first if:
          </p>
          <ul>
            <li>You have sudden, severe pain from an unknown cause</li>
            <li>You have numbness, weakness, or tingling</li>
            <li>You have unexplained swelling</li>
            <li>You're on blood thinners or have a bleeding disorder</li>
            <li>You have a recent injury needing imaging</li>
          </ul>
          <p>
            For everything else — the tension, the tightness, the stress, the sleep — massage is a genuinely great next step.
          </p>

          <h2>What to Book if You Just Realized You Need One</h2>
          <ul>
            <li><strong>Stress-focused, first-time, or not in pain:</strong> Swedish, 60 or 90 minutes</li>
            <li><strong>Chronic tension or specific tight areas:</strong> deep tissue or a therapeutic blend, 60 or 90 minutes</li>
            <li><strong>You're not sure:</strong> text me. We'll figure it out together.</li>
          </ul>

          <h2>What Happens When You Actually Book</h2>
          <p>
            You'll come in, do a short intake, get on a warm table, and let someone who knows what they're doing take care of you for an hour. When you leave, you'll usually feel two things: physically lighter, and a little surprised at how much you were carrying. That surprise is data.
          </p>

          {/* CTA */}
          <div className="not-prose bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 text-white mt-12">
            <h3 className="text-2xl font-serif mb-4">Stop Wondering and Come In</h3>
            <p className="mb-6">Midtown Sacramento, 2528 I Street. If you've been thinking about it, that's usually enough of an answer.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book" className="inline-block bg-white text-teal-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-300 text-center" data-testid="cta-book-now">Book a Session</Link>
              <Link to="/about" className="inline-block bg-teal-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-teal-900 transition-colors duration-300 text-center" data-testid="cta-about">A Little About Me</Link>
            </div>
          </div>
        </div>

        <MidtownStudioCallout />
        <BlogEmailCapture source="how-do-i-know" />

        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-start space-x-6">
            <img src="https://customer-assets.emergentagent.com/job_suzuki-2026-refresh/artifacts/aautielc_Ayumi.jpg" alt="Ayumi Suzuki" className="w-24 h-24 rounded-full object-cover" />
            <div>
              <h3 className="text-2xl font-serif text-gray-800 mb-2">— Ayumi</h3>
              <p className="text-gray-600 mb-4">
                California CAMTC-certified. 2528 I Street, midtown Sacramento. If you've been quietly asking your body for a break, this is me telling you to give it one.
              </p>
              <Link to="/about" className="text-teal-600 hover:text-teal-700 font-medium">Read my longer story →</Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPostHowDoIKnow;
